import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import {
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { FieldArray, getIn, useField, useFormikContext } from "formik";
import { FC, ReactElement, useCallback } from "react";

import { FormikSelectField } from "~/src/components/common/forms/FormikSelectField";
import { HelpIcon } from "~/src/components/common/HelpIcon";
import { IconButton } from "~/src/components/common/IconButton";
import { TextField } from "~/src/components/common/TextField";

import { UrlMappingFormValues } from "./presentations/UrlMappingForm";
import {
  ConditionRow,
  ConditionRowKind,
  conditionRowKindOptions,
  defaultMatchModeForKind,
  emptyConditionRow,
  matchModeOptionsForKind,
} from "./urlMappingFormUtils";

const matchModeHelpText = (
  kind: ConditionRowKind,
  matchMode: string
): string | undefined => {
  if (kind === "query_param" && matchMode === "regex") {
    return "Ruby 正規表現。複数値の OR は ^value1$|^value2$ のように | で連結";
  }
  return undefined;
};

const FieldLabel: FC<{ label: string; help?: string | ReactElement }> = ({
  label,
  help,
}) => (
  <HStack spacing="4px" mb="4px">
    <Text fontSize="xs" color="gray.600">
      {label}
    </Text>
    {help ? <HelpIcon text={help} /> : null}
  </HStack>
);

/** Show yup errors while form is dirty so empty new rows explain a disabled Save. */
const ConditionTextField: FC<{
  name: string;
  placeholder?: string;
}> = ({ name, placeholder }) => {
  const [{ value, onBlur, onChange }, { error }] = useField<string>(name);
  const { dirty } = useFormikContext();

  return (
    <TextField
      type="text"
      mode="fill"
      name={name}
      value={value}
      placeholder={placeholder}
      error={dirty ? error : undefined}
      onBlur={onBlur}
      onChange={onChange}
    />
  );
};

const valuePlaceholder = (
  kind: ConditionRowKind,
  matchMode: string
): string => {
  if (kind === "query_param" && matchMode === "regex") {
    return "lotion007_.* または ^p_value1$|^p_value2$";
  }
  if (kind === "path" && matchMode === "prefix") {
    return "/campaigns/lp1/test_";
  }
  if (kind === "query_substring") return "例: &us=pc_m";
  if (kind === "referrer") return "partner.example.com";
  if (kind === "title") return "見積り結果";
  if (kind === "environment") return "production または production|staging";
  return "値";
};

export const UrlMappingConditionsEditor: FC = () => {
  const { values, setFieldValue, setFieldTouched, errors, dirty } =
    useFormikContext<UrlMappingFormValues>();

  const handleKindChange = useCallback(
    (index: number, kind: ConditionRowKind) => {
      setFieldValue(`conditionRows.${index}.kind`, kind);
      setFieldValue(
        `conditionRows.${index}.matchMode`,
        defaultMatchModeForKind(kind)
      );
      if (kind !== "query_param") {
        setFieldValue(`conditionRows.${index}.key`, "");
      }
    },
    [setFieldValue]
  );

  const handleAddCondition = useCallback(
    (push: (row: ReturnType<typeof emptyConditionRow>) => void) => {
      const index = values.conditionRows.length;
      push(emptyConditionRow());
      void Promise.resolve().then(() => {
        setFieldTouched(`conditionRows.${index}.key`, true, false);
        setFieldTouched(`conditionRows.${index}.value`, true, true);
      });
    },
    [setFieldTouched, values.conditionRows.length]
  );

  const conditionRowErrors = getIn(errors, "conditionRows");
  const hasConditionErrors =
    dirty &&
    Array.isArray(conditionRowErrors) &&
    conditionRowErrors.some(
      (row) =>
        row != null && typeof row === "object" && Object.keys(row).length > 0
    );

  return (
    <VStack align="stretch" spacing="12px" width="100%">
      <FieldArray name="conditionRows">
        {({ push, remove }) => (
          <>
            {values.conditionRows.length === 0 && (
              <Text fontSize="sm" color="gray.500">
                条件なし — 「+ 条件を追加」で行を追加してください。
              </Text>
            )}

            {values.conditionRows.map((row: ConditionRow, index: number) => {
              const matchModeOptions = matchModeOptionsForKind(row.kind);
              const showKey = row.kind === "query_param";
              const showMatchMode = matchModeOptions.length > 1;
              const matchHelp = matchModeHelpText(row.kind, row.matchMode);

              return (
                <Box
                  key={`condition-row-${index}`}
                  borderWidth="1px"
                  borderRadius="md"
                  p="12px"
                  bg="gray.50"
                >
                  <HStack align="flex-start" spacing="8px" flexWrap="wrap">
                    <Box minW="160px" flex="1 1 160px">
                      <FieldLabel label="種別" />
                      <FormikSelectField
                        name={`conditionRows.${index}.kind`}
                        options={conditionRowKindOptions}
                        // eslint-disable-next-line react/jsx-no-bind
                        onSetStateAction={(kind) =>
                          handleKindChange(index, kind as ConditionRowKind)
                        }
                      />
                    </Box>

                    {showMatchMode && (
                      <Box minW="160px" flex="1 1 160px">
                        <FieldLabel label="マッチ" help={matchHelp} />
                        <FormikSelectField
                          name={`conditionRows.${index}.matchMode`}
                          options={matchModeOptions}
                        />
                      </Box>
                    )}

                    {showKey && (
                      <Box minW="120px" flex="1 1 120px">
                        <FieldLabel label="キー" />
                        <ConditionTextField
                          name={`conditionRows.${index}.key`}
                          placeholder="p"
                        />
                      </Box>
                    )}

                    <Box minW="200px" flex="2 1 200px">
                      <FieldLabel label="値" />
                      <ConditionTextField
                        name={`conditionRows.${index}.value`}
                        placeholder={valuePlaceholder(row.kind, row.matchMode)}
                      />
                    </Box>

                    <Box pt="22px">
                      <IconButton
                        size="24px"
                        type="button"
                        icon={<DeleteIcon fontSize="small" />}
                        label="条件を削除"
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={() => remove(index)}
                      />
                    </Box>
                  </HStack>
                </Box>
              );
            })}

            {hasConditionErrors && (
              <Text fontSize="sm" color="red.500">
                条件のキー・値を入力するか、不要な条件行を削除してください。
              </Text>
            )}

            <Button
              size="sm"
              variant="outline"
              alignSelf="flex-start"
              leftIcon={<AddCircleIcon fontSize="small" />}
              // eslint-disable-next-line react/jsx-no-bind
              onClick={() => handleAddCondition(push)}
            >
              条件を追加
            </Button>
          </>
        )}
      </FieldArray>
    </VStack>
  );
};
